import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { authManager } from '../utils/auth';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

// Sign-up monitoring + referral program (somo-circle1-api /admin/referrals).
// Sign-ups are counted whether or not the referral_program flag is on; the
// referral sections fill in once it is. The program itself (on/off, staff
// rollout, signupPoints, premiumPoints, monthlyCap) is managed on Program
// Flags: points when a friend signs up, when they join Premium, or both.

export const SOURCE_LABELS = {
    referral: 'Referral link',
    sos_email: 'SOS email',
    paid_ads: 'Paid ads',
    other_campaign: 'Other campaign',
    organic: 'Organic / direct',
};

export const REJECT_LABELS = {
    self_referral: 'Own code',
    same_person: 'Same person',
    unknown_code: 'Unknown code',
    referrer_inactive: 'Referrer inactive',
};

export const REWARD_LABELS = {
    // Being booked right now. An abandoned booking comes back from the API as
    // 'failed', so it gets the Retry button.
    pending: 'Booking…',
    booked: 'points',
    capped: 'Monthly cap reached',
    none: 'No reward set',
    failed: 'Failed',
};

// One reward cell: "25 points", "10 points (annual cap)", "Monthly cap
// reached", ... or "—" when that moment hasn't happened yet.
export const rewardText = (reward) => {
    if (!reward || !reward.status) return '—';
    if (reward.status === 'booked') {
        // clamped: the ledger booked fewer points than the setting because the
        // referrer is at their annual loyalty cap.
        return reward.clamped ? `${reward.points} points (annual cap)` : `${reward.points} points`;
    }
    return REWARD_LABELS[reward.status] || reward.status;
};

const hasFailedReward = (r) => r.signupReward?.status === 'failed' || r.premiumReward?.status === 'failed';

const isoDay = (date) => date.toISOString().slice(0, 10);

const defaultRange = () => {
    const to = new Date();
    const from = new Date(to.getTime() - 29 * 24 * 60 * 60 * 1000);
    return { from: isoDay(from), to: isoDay(to) };
};

// The API's row caps (somo-circle1-api referral.controller.js).
export const REFERRER_LIMIT = 200;
export const REFERRAL_LIMIT = 500;

// Parse a response body, turning a non-JSON one (an HTML 404/502 from a proxy,
// or the API not deployed yet) into a readable message instead of
// "Unexpected token '<'".
export const readJson = async (res) => {
    try {
        return await res.json();
    } catch {
        throw new Error(`Request failed (HTTP ${res.status})`);
    }
};

export const rate = (part, whole) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—');

const personLabel = (p) => (p ? (p.name ? `${p.name} (${p.email || '—'})` : p.email || '—') : '—');

function Referrals() {
    const [range, setRange] = useState(defaultRange);
    // What the tables currently show: the range AND status last applied, so a
    // reload (e.g. after Retry) never picks up an un-applied dropdown value.
    const [applied, setApplied] = useState(() => ({ ...defaultRange(), status: '' }));
    const [statusFilter, setStatusFilter] = useState('');
    const [signups, setSignups] = useState(null);
    const [referrers, setReferrers] = useState([]);
    const [referrals, setReferrals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [retrying, setRetrying] = useState(null);
    const [loadError, setLoadError] = useState('');
    // Only the latest load may set state: an older response finishing after a
    // newer one must not overwrite it.
    const loadSeq = useRef(0);

    const getJson = async (path) => {
        const res = await authManager.authenticatedFetch(`${Base_Url}${path}`);
        const data = await readJson(res);
        if (data.status !== 'success') {
            throw new Error(data.error?.message || data.message || `Request failed (HTTP ${res.status})`);
        }
        return data.result;
    };

    const load = async (filters = applied) => {
        loadSeq.current += 1;
        const seq = loadSeq.current;
        setLoading(true);
        setLoadError('');
        const { from, to, status } = filters;
        const qs = new URLSearchParams({ from, to });
        const listQs = new URLSearchParams({ from, to, ...(status && { status }) });
        try {
            const [s, rf, rl] = await Promise.all([
                getJson(`/admin/referrals/signups?${qs}`),
                getJson(`/admin/referrals/referrers?${qs}`),
                getJson(`/admin/referrals?${listQs}`),
            ]);
            if (seq !== loadSeq.current) return;
            setSignups(s);
            setReferrers(rf.referrers || []);
            setReferrals(rl.referrals || []);
        } catch (error) {
            if (seq !== loadSeq.current) return;
            console.error('Error fetching sign-ups and referrals:', error);
            setLoadError(error.message || 'Error fetching sign-ups and referrals');
            toast.error(error.message || 'Error fetching sign-ups and referrals');
        } finally {
            if (seq === loadSeq.current) setLoading(false);
        }
    };

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleApply = (e) => {
        e.preventDefault();
        const next = { ...range, status: statusFilter };
        setApplied(next);
        load(next);
    };

    const handleRetry = async (id) => {
        setRetrying(id);
        try {
            const res = await authManager.authenticatedFetch(`${Base_Url}/admin/referrals/${id}/retry`, { method: 'POST' });
            const data = await readJson(res);
            if (data.status !== 'success') throw new Error(data.error?.message || `Retry failed (HTTP ${res.status})`);
            if (hasFailedReward(data.result)) toast.warning('A reward is still not booked — try again later');
            else toast.success('Reward booked');
            await load();
        } catch (error) {
            toast.error(error.message || 'Retry failed');
        } finally {
            setRetrying(null);
        }
    };

    const sources = signups?.sources || Object.keys(SOURCE_LABELS);
    const totals = signups?.totals || {};
    const sum = (key) => sources.reduce((n, s) => n + (totals[s]?.[key] || 0), 0);

    return (
        <>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-8">
                            <h1 className="m-0 txt_lft">Sign-ups &amp; Referrals</h1>
                        </div>
                        <div className="col-sm-4 text-right">
                            <Link to="/programFlags" className="btn btn-outline-secondary btn-sm">
                                Reward points &amp; cap (Program Flags → referral_program)
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    <div className="card mb-3">
                        <div className="card-body">
                            <form onSubmit={handleApply} className="row align-items-end">
                                <div className="col-md-3">
                                    <label htmlFor="ref-from">From</label>
                                    <input id="ref-from" type="date" className="form-control" value={range.from}
                                        onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
                                </div>
                                <div className="col-md-3">
                                    <label htmlFor="ref-to">To</label>
                                    <input id="ref-to" type="date" className="form-control" value={range.to}
                                        onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
                                </div>
                                <div className="col-md-3">
                                    <label htmlFor="ref-status">Referral status</label>
                                    <select id="ref-status" className="form-control" value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}>
                                        <option value="">All</option>
                                        <option value="confirmed">Confirmed</option>
                                        <option value="premium">Joined Premium</option>
                                        <option value="rejected">Rejected</option>
                                        <option value="failed">Reward failed</option>
                                        <option value="capped">Monthly cap reached</option>
                                    </select>
                                </div>
                                <div className="col-md-3 mt-2">
                                    <button type="submit" className="btn btn-primary">Apply</button>
                                </div>
                                <div className="col-md-12 mt-2">
                                    <small className="text-muted">
                                        Days are UTC, at most 180 days. Referrals are listed by the day the friend completed sign-up.
                                    </small>
                                </div>
                            </form>
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center p-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                        </div>
                    ) : loadError ? (
                        // Don't show empty tables after a failed load: they
                        // would read as "no sign-ups" rather than "not loaded".
                        <div className="alert alert-danger" role="alert">
                            Couldn&apos;t load sign-ups and referrals: {loadError}. Try Apply again.
                        </div>
                    ) : (
                        <>
                            <div className="card mb-3">
                                <div className="card-header"><h3 className="card-title">Sign-ups by source ({applied.from} to {applied.to})</h3></div>
                                <div className="card-body table-responsive p-0">
                                    <table className="table table-striped table-bordered txt_lft mb-0">
                                        <thead>
                                            <tr>
                                                <th>Source</th>
                                                <th>Sign-ups</th>
                                                <th>Verified email</th>
                                                <th>Premium now</th>
                                                <th>Sign-up → Premium</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sources.map((s) => {
                                                const t = totals[s] || { signups: 0, verified: 0, premium: 0 };
                                                return (
                                                    <tr key={s}>
                                                        <td>{SOURCE_LABELS[s] || s}</td>
                                                        <td>{t.signups}</td>
                                                        <td>{t.verified} ({rate(t.verified, t.signups)})</td>
                                                        <td>{t.premium}</td>
                                                        <td>{rate(t.premium, t.signups)}</td>
                                                    </tr>
                                                );
                                            })}
                                            <tr className="font-weight-bold">
                                                <td>Total</td>
                                                <td>{sum('signups')}</td>
                                                <td>{sum('verified')} ({rate(sum('verified'), sum('signups'))})</td>
                                                <td>{sum('premium')}</td>
                                                <td>{rate(sum('premium'), sum('signups'))}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="card mb-3">
                                <div className="card-header"><h3 className="card-title">Sign-ups per day</h3></div>
                                <div className="card-body table-responsive p-0" style={{ maxHeight: 360 }}>
                                    <table className="table table-sm table-striped table-bordered txt_lft mb-0">
                                        <thead>
                                            <tr>
                                                <th>Day</th>
                                                {sources.map((s) => <th key={s}>{SOURCE_LABELS[s] || s}</th>)}
                                                <th>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[...(signups?.days || [])].reverse().map(({ day, bySource }) => (
                                                <tr key={day}>
                                                    <td>{day}</td>
                                                    {sources.map((s) => <td key={s}>{bySource[s]?.signups || 0}</td>)}
                                                    <td>{sources.reduce((n, s) => n + (bySource[s]?.signups || 0), 0)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="card mb-3">
                                <div className="card-header">
                                    <h3 className="card-title">Top referrers</h3>
                                    {referrers.length >= REFERRER_LIMIT && (
                                        <small className="text-muted ml-2">Showing the top {REFERRER_LIMIT}; narrow the dates to see others.</small>
                                    )}
                                </div>
                                <div className="card-body table-responsive p-0">
                                    {referrers.length === 0 ? (
                                        <p className="p-3 mb-0 text-muted">No referred sign-ups in this range.</p>
                                    ) : (
                                        <table className="table table-striped table-bordered txt_lft mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Member</th>
                                                    <th>Code</th>
                                                    <th>Friends joined</th>
                                                    <th>Joined Premium</th>
                                                    <th>Rejected</th>
                                                    <th>Capped</th>
                                                    <th>Failed</th>
                                                    <th>Points</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {referrers.map((r) => (
                                                    <tr key={r.userId}>
                                                        <td><Link to={`/users/${r.userId}`}>{personLabel(r)}</Link></td>
                                                        <td><code>{r.code || '—'}</code></td>
                                                        <td>{r.joined}</td>
                                                        <td>{r.premium}</td>
                                                        <td className={r.rejected > 0 ? 'text-danger' : ''}>{r.rejected}</td>
                                                        <td>{r.capped}</td>
                                                        <td className={r.failed > 0 ? 'text-danger' : ''}>{r.failed}</td>
                                                        <td>{r.points}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>

                            <div className="card mb-3">
                                <div className="card-header">
                                    <h3 className="card-title">Referrals</h3>
                                    {referrals.length >= REFERRAL_LIMIT && (
                                        <small className="text-muted ml-2">Showing the newest {REFERRAL_LIMIT}; narrow the dates to see older ones.</small>
                                    )}
                                </div>
                                <div className="card-body table-responsive p-0">
                                    {referrals.length === 0 ? (
                                        <p className="p-3 mb-0 text-muted">No referrals in this range.</p>
                                    ) : (
                                        <table className="table table-striped table-bordered txt_lft mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Signed up</th>
                                                    <th>Friend</th>
                                                    <th>Referred by</th>
                                                    <th>Code</th>
                                                    <th>Status</th>
                                                    <th>Sign-up reward</th>
                                                    <th>Premium</th>
                                                    <th>Premium reward</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {referrals.map((r) => (
                                                    <tr key={r.id}>
                                                        <td>{r.joinedAt ? new Date(r.joinedAt).toLocaleString() : '—'}</td>
                                                        <td>{personLabel(r.referred)}</td>
                                                        <td>{personLabel(r.referrer)}</td>
                                                        <td><code>{r.code}</code></td>
                                                        <td>
                                                            {r.status === 'rejected'
                                                                ? <span className="badge badge-danger">Rejected: {REJECT_LABELS[r.rejectReason] || r.rejectReason}</span>
                                                                : <span className="badge badge-success">Confirmed</span>}
                                                        </td>
                                                        <td className={r.signupReward?.status === 'failed' ? 'text-danger' : ''}>{rewardText(r.signupReward)}</td>
                                                        <td>{r.premiumAt ? new Date(r.premiumAt).toLocaleDateString() : 'Not yet'}</td>
                                                        <td className={r.premiumReward?.status === 'failed' ? 'text-danger' : ''}>{rewardText(r.premiumReward)}</td>
                                                        <td>
                                                            {hasFailedReward(r) && (
                                                                <button type="button" className="btn btn-sm btn-warning"
                                                                    disabled={retrying === r.id}
                                                                    onClick={() => handleRetry(r.id)}>
                                                                    {retrying === r.id ? 'Retrying…' : 'Retry reward'}
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default Referrals;
